import React, { useState } from 'react';
import AppHeader from '../../../components/AppHeader.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faEnvelope, faPhone, faCalendar, faMapMarkerAlt, faSave, faTimes, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

const NewCustomerPage: React.FC = () => {
  const [formData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    address: '',
    city: '',
    postalCode: '',
    notes: '',
    preferences: {
      preferredTime: '',
      preferredServices: '',
      communicationMethod: 'email',
    },
  });

  const breadcrumb = [{ label: 'Zákazníci' }, { label: 'Nový zákazník' }];

  const handleInputChange = (field: string, value: string) => {
    console.log('Noted change in field:', field, 'with value:', value);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Creating customer:', formData);
    // Handle form submission
  };

  const isFormValid = formData.firstName && formData.lastName && (formData.email || formData.phone);

  return (
    <div className="h-full flex flex-col">
      <AppHeader title="Nový zákazník" breadcrumb={breadcrumb} />

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Nový zákazník</h2>
                <p className="text-gray-600 mt-1">Vyplňte informace o novém zákazníkovi</p>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-8">
                {/* Personal Information */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <FontAwesomeIcon icon={faUser} className="mr-2 text-green-600" />
                    Osobní informace
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Jméno *</label>
                      <input
                        type="text"
                        required
                        value={formData.firstName}
                        onChange={e => handleInputChange('firstName', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                        placeholder="Zadejte jméno"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Příjmení *</label>
                      <input
                        type="text"
                        required
                        value={formData.lastName}
                        onChange={e => handleInputChange('lastName', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                        placeholder="Zadejte příjmení"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <FontAwesomeIcon icon={faCalendar} className="mr-1" />
                        Datum narození
                      </label>
                      <input
                        type="date"
                        value={formData.dateOfBirth}
                        onChange={e => handleInputChange('dateOfBirth', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <FontAwesomeIcon icon={faEnvelope} className="mr-2 text-blue-600" />
                    Kontaktní informace
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <FontAwesomeIcon icon={faPhone} className="mr-1" />
                        Telefon *
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={e => handleInputChange('phone', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                        placeholder="+420 123 456 789"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <FontAwesomeIcon icon={faEnvelope} className="mr-1" />
                        E-mail *
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={e => handleInputChange('email', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                        placeholder="zákazník@email.cz"
                      />
                    </div>
                  </div>
                </div>

                {/* Address Information */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2 text-purple-600" />
                    Adresa
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Ulice a číslo</label>
                      <input
                        type="text"
                        value={formData.address}
                        onChange={e => handleInputChange('address', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                        placeholder="Václavské náměstí 1"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Město</label>
                        <input
                          type="text"
                          value={formData.city}
                          onChange={e => handleInputChange('city', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                          placeholder="Praha"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">PSČ</label>
                        <input
                          type="text"
                          value={formData.postalCode}
                          onChange={e => handleInputChange('postalCode', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                          placeholder="110 00"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Preferences */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <FontAwesomeIcon icon={faInfoCircle} className="mr-2 text-yellow-600" />
                    Preference
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Preferovaný čas návštěv</label>
                      <select
                        value={formData.preferences.preferredTime}
                        onChange={e => handleInputChange('preferences.preferredTime', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                      >
                        <option value="">Vyberte preferovaný čas</option>
                        <option value="morning">Ráno (8:00-12:00)</option>
                        <option value="afternoon">Odpoledne (12:00-16:00)</option>
                        <option value="evening">Večer (16:00-20:00)</option>
                        <option value="any">Bez preferencí</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Způsob komunikace</label>
                      <select
                        value={formData.preferences.communicationMethod}
                        onChange={e => handleInputChange('preferences.communicationMethod', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                      >
                        <option value="email">E-mail</option>
                        <option value="sms">SMS</option>
                        <option value="phone">Telefon</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Preferované služby</label>
                    <input
                      type="text"
                      value={formData.preferences.preferredServices}
                      onChange={e => handleInputChange('preferences.preferredServices', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                      placeholder="Střih, barvení, kosmetické ošetření..."
                    />
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Poznámky</label>
                  <textarea
                    rows={4}
                    value={formData.notes}
                    onChange={e => handleInputChange('notes', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                    placeholder="Přidejte poznámky o zákazníkovi..."
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button type="button" className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors">
                    <FontAwesomeIcon icon={faTimes} className="w-4 h-4 mr-2" />
                    Zrušit
                  </button>
                  <button
                    type="submit"
                    disabled={!isFormValid}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    <FontAwesomeIcon icon={faSave} className="w-4 h-4 mr-2" />
                    Vytvořit zákazníka
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewCustomerPage;
